import { useQueryClient } from '@tanstack/react-query';
import type { PropsWithChildren } from 'react';
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
} from 'react';
import type { CmsSignpost } from '@gov-nx/api';
import {
	cmsSignpostGroupsQuery,
	cmsSignpostQuery,
	useAxiosCmsInstance,
	usePoQuery,
} from '@gov-nx/api';
import { useExtraConfigContext } from '@gov-nx/core/app';
import { useAppLanguage, useConnection } from '@gov-nx/core/hooks';
import type { Nullable, Optional } from '@gov-nx/core/types';
import { is, isNonEmpty } from '@gov-nx/core/types';
import {
	useAuthStore,
	useSignpostStore,
	useUserStoreFavorites,
} from '@gov-nx/store';
import { useBoolean, useDebounce, useFilter } from '@gov-nx/utils/common';
import { FormInstance, prepareSubmitData } from './FormDefinitions';
import type { SignpostContext, SignpostListFilter } from './context.types';
import { isFavorite, signpostComparator, useCMSActionResolver } from './utils';

const RozcestikContext = createContext<Nullable<SignpostContext>>(null);

const OFFLINE_SERVICES = ['DROZD'];

type RozcestnikContextProviderProps = PropsWithChildren<{
	isFavoritesSelected?: boolean;
	persistFilters?: boolean;
}>;

export function RozcestnikContextProvider({
	children,
	isFavoritesSelected,
	persistFilters = true,
}: RozcestnikContextProviderProps) {
	const favorites = useUserStoreFavorites();
	const {
		extraConfig: { environment, application },
	} = useExtraConfigContext();
	const { language } = useAppLanguage();
	const activeReza = useAuthStore('activeReza');
	const activeRezaDesignation = useAuthStore('activeRezaDesignation');
	const { isOnline, isOffline } = useConnection();
	const localSignposts = useSignpostStore('signposts');
	const saveSignposts = useSignpostStore('saveSignposts');
	const queryClient = useQueryClient();
	const searchTerm = useDebounce<string>(200);
	const { axiosCmsBaseInstance, axiosCmsInstance } = useAxiosCmsInstance();

	const { filter, setPartialFilter } = useFilter<SignpostListFilter>({
		key: 'rozcestnik',
		persist: persistFilters,
		initialState: {
			language: language,
			serviceGroups: null,
			favourites: isFavoritesSelected ?? false,
			isSortedAlphabetically: false,
			page: 1,
			pageSize: 10,
		},
	});
	const isSortedAlphabetically = useBoolean(filter.isSortedAlphabetically);

	const { data, isFetching } = usePoQuery<Optional<CmsSignpost[]>>({
		queryKey: ['signpost', filter, searchTerm.value, activeRezaDesignation],
		queryFn: async (): Promise<CmsSignpost[]> => {
			if (!application || !environment) {
				return [];
			}

			const params = prepareSubmitData(
				filter,
				searchTerm.value,
				application,
				environment,
				activeRezaDesignation ?? undefined
			);
			const data = await cmsSignpostQuery(axiosCmsBaseInstance, params);
			const filteredSignposts = data.filter(isNonEmpty);
			saveSignposts(
				filteredSignposts.filter((signpost) =>
					OFFLINE_SERVICES.includes(signpost.name)
				)
			);
			return filteredSignposts;
		},
	});

	const { withAction } = useCMSActionResolver();

	const getSignposts = () => {
		if (isOffline) {
			return localSignposts;
		}

		return data;
	};

	const signposts = [...(getSignposts() ?? [])]
		.filter((signpost) => {
			if (filter.favourites) {
				return isFavorite(favorites)(signpost);
			}
			return true;
		})
		.sort(
			signpostComparator(searchTerm.hasValue, isSortedAlphabetically.value)
		);

	const paginatedSignposts: SignpostContext['data'] = signposts
		.slice((filter.page - 1) * filter.pageSize, filter.page * filter.pageSize)
		.map(withAction)
		.filter(is);

	const signpostGroupQuery = usePoQuery({
		queryKey: ['signpost-group', language],
		queryFn: () =>
			cmsSignpostGroupsQuery(axiosCmsInstance, { locale: language }),
		refetchOnWindowFocus: false,
		retry: 0,
	});

	const groups = (signpostGroupQuery.data ?? []).sort(
		(a, b) => a.order - b.order
	);

	const goToPage = useCallback(
		(page: number) => {
			setPartialFilter({ page: page });
		},
		[setPartialFilter]
	);

	const formDefinition = FormInstance();
	const onSubmit = formDefinition.formMethods.handleSubmit(() => null);

	const setFilterBase = (groupId: number | null, favourites = false) => {
		setPartialFilter({ serviceGroups: groupId, favourites, page: 1 });
		searchTerm.reset();
		formDefinition.formReset();
	};

	const setFilterActiveGroup = (groupId: number) => {
		setFilterBase(groupId);
	};
	const setFilterFavourites = () => {
		setFilterBase(null, true);
	};
	const setFilterAll = () => {
		setFilterBase(null, false);
	};

	const onToggleSorting = () => {
		setPartialFilter({ isSortedAlphabetically: !isSortedAlphabetically.value });
		isSortedAlphabetically.toggle();
	};

	const loadMore = (howMany: number) =>
		setPartialFilter({
			page: 1,
			pageSize: filter.pageSize + howMany,
		});

	const numberOfSignposts = signposts.length;

	const canLoadMore = numberOfSignposts > filter.pageSize;

	useEffect(() => {
		if (language !== filter.language) {
			setPartialFilter({ serviceGroups: null, language });
		}
	}, [filter.language, language, setPartialFilter]);

	useEffect(() => {
		const subscription = formDefinition.formMethods.watch((value) => {
			const searchValue = value.search;
			if (searchValue && searchValue.length > 2) {
				setPartialFilter({ serviceGroups: null, favourites: false, page: 1 });
				searchTerm.update(searchValue);
			} else {
				searchTerm.reset();
			}
		});
		return () => subscription.unsubscribe();
	}, [formDefinition.formMethods, searchTerm, setPartialFilter]);

	useEffect(() => {
		queryClient.invalidateQueries({ queryKey: ['signpost'] });
	}, [isOnline, queryClient]);

	const favoriteSignposts = (data?.filter(isFavorite(favorites)) ?? [])
		.map(withAction)
		.filter(is);

	return (
		<RozcestikContext.Provider
			value={{
				setFilterActiveGroup,
				setFilterFavourites,
				setFilterAll,
				loadMore,
				canLoadMore,
				groups,
				numberOfSignposts,
				filter,
				data: paginatedSignposts,
				favoriteSignposts,
				isSignpostDataFetching: isFetching,
				formDefinition,
				onSubmit,
				goToPage,
				favorites,
				onToggleSorting,
				isSortedAlphabetically: isSortedAlphabetically.value,
				isFO: activeReza?.zastupujiciOsobaTyp === 'FO',
			}}>
			{children}
		</RozcestikContext.Provider>
	);
}

export const useRozcestnikContext = (): SignpostContext => {
	const context = useContext(RozcestikContext);
	if (!context) {
		throw new Error(
			'useRozcestnikContext must be used within a RozcestnikContextProvider'
		);
	}
	return context;
};
